/**
 * Challenge Validator
 * Pure functions for challenge validation
 */

import { Challenge, ChallengeParticipation, User } from '../../types';
import { isValidDateString, isDateInRange } from '../../utils/dates';
import { parseDate } from '../../utils/dates';

/**
 * Check if challenge is currently active
 */
export function isChallengeActive(challenge: Challenge): boolean {
    if (!challenge.isActive) return false;

    const now = new Date();
    const startDate = parseDate(challenge.startDate);
    const endDate = parseDate(challenge.endDate);

    return isDateInRange(now, startDate, endDate);
}

/**
 * Check if challenge has started
 */
export function hasChallengeStarted(challenge: Challenge): boolean {
    const now = new Date();
    const startDate = parseDate(challenge.startDate);
    return now >= startDate;
}

/**
 * Check if challenge has ended
 */
export function hasChallengeEnded(challenge: Challenge): boolean {
    const now = new Date();
    const endDate = parseDate(challenge.endDate);
    return now > endDate;
}

/**
 * Check if user is eligible for challenge
 */
export function isUserEligibleForChallenge(
    challenge: Challenge,
    user: User
): { eligible: boolean; reason?: string } {
    // Check if challenge is active
    if (!isChallengeActive(challenge)) {
        return { eligible: false, reason: 'Challenge is not active' };
    }

    // Check organization match
    if (challenge.organizationId) {
        if (!user.organizationId || user.organizationId !== challenge.organizationId) {
            return {
                eligible: false,
                reason: 'Challenge is restricted to specific organization',
            };
        }
    }

    // Check team match
    if (challenge.teamId) {
        if (!user.teamId || user.teamId !== challenge.teamId) {
            return {
                eligible: false,
                reason: 'Challenge is restricted to specific team',
            };
        }
    }

    // Check challenge level
    if (challenge.level === 'organization' && !user.organizationId) {
        return {
            eligible: false,
            reason: 'Organization membership required',
        };
    }

    if (challenge.level === 'team' && !user.teamId) {
        return {
            eligible: false,
            reason: 'Team membership required',
        };
    }

    return { eligible: true };
}

/**
 * Check if user can join challenge
 */
export function canUserJoinChallenge(
    challenge: Challenge,
    user: User,
    existingParticipations: ChallengeParticipation[]
): { canJoin: boolean; reason?: string } {
    // Check eligibility
    const eligibility = isUserEligibleForChallenge(challenge, user);
    if (!eligibility.eligible) {
        return { canJoin: false, reason: eligibility.reason };
    }

    // Check if already participating
    const existingParticipation = existingParticipations.find(
        (p) => p.challengeId === challenge.id
    );

    if (existingParticipation) {
        if (existingParticipation.status === 'in_progress') {
            return { canJoin: false, reason: 'Already participating in this challenge' };
        }
        if (existingParticipation.status === 'completed') {
            if (!challenge.allowMultipleEntries) {
                return { canJoin: false, reason: 'Challenge already completed' };
            }
        }
    }

    return { canJoin: true };
}

/**
 * Validate challenge data
 */
export function validateChallenge(challenge: Partial<Challenge>): {
    valid: boolean;
    errors: string[];
} {
    const errors: string[] = [];

    if (!challenge.title || challenge.title.trim().length === 0) {
        errors.push('Title is required');
    }

    if (!challenge.description || challenge.description.trim().length === 0) {
        errors.push('Description is required');
    }

    if (!challenge.targetValue || challenge.targetValue <= 0) {
        errors.push('Target value must be greater than 0');
    }

    if (!challenge.points || challenge.points <= 0) {
        errors.push('Points must be greater than 0');
    }

    if (!challenge.startDate || !isValidDateString(challenge.startDate)) {
        errors.push('Valid start date is required');
    }

    if (!challenge.endDate || !isValidDateString(challenge.endDate)) {
        errors.push('Valid end date is required');
    }

    if (challenge.startDate && challenge.endDate) {
        const startDate = parseDate(challenge.startDate);
        const endDate = parseDate(challenge.endDate);

        if (endDate < startDate) {
            errors.push('End date must be after start date');
        }
    }

    if (!challenge.metric) {
        errors.push('Metric is required');
    }

    if (!challenge.category) {
        errors.push('Category is required');
    }

    if (!challenge.level) {
        errors.push('Level is required');
    }

    if (!challenge.type) {
        errors.push('Type is required');
    }

    if (!challenge.duration) {
        errors.push('Duration is required');
    }

    return {
        valid: errors.length === 0,
        errors,
    };
}

